import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FormProvider } from 'react-hook-form';
import {
  ContainerBox,
  FormCard,
  FormContentContainer,
} from '../../../core/components/styled/StyledComponents';
import {
  ENROLLMENT_FORM_STEPS,
  ENROLLMENT_FORM_STEPS2,
  EnrollmentSteps,
} from '../../../core/constant/constant';
import { ErrorSource } from '../../../core/constant/model/api/api';
import ApiErrorLabel from '../../../core/components/ui/ApiErrorLabel';
import { useAuth } from '../../auth/hooks/useAuth';
import { stepConfig, stepConfig2 } from '../utils/enrollmentStepConfig';
import { EnrollmentFormData } from '../types/enrollment';
import { useDispatch, useSelector } from 'react-redux';
import { clearApiError } from '../../../core/store/slices/apiError.slice';

import FormSidebar from './FormSidebar';
import MobileHeader from './MobileHeader';
import FormNavigation from './FormNavigation';
import { useEnrollmentForm, useApiErrors, useEnrollmentSubmission } from '../hooks/enrollmentHooks';
import StepContent, { StepContent2 } from './steps/StepContent';
import { useProfileQuery } from '../../auth/hooks/useProfileQuery';
import { RootState } from '../../../core/store';
import { setEnrollmentStatus } from '../../../core/store/slices/enrollment.slice';
import { useCompleteEnrollmentMutation } from '../hooks/enrollmentApi';
import { UserStatus } from '../../../core/constant/constant';

const MindRightEnrollment2: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { userId } = useAuth();
  const formContainerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const profileQuery = useProfileQuery();
  const { activeStep, setActiveStep, isSaving, setIsSaving, methods, getProgressValue } =
    useEnrollmentForm();

  const { hasApiError, getApiErrorBySource } = useApiErrors();
  const { submitStepData } = useEnrollmentSubmission(userId);
  const completeEnrollmentMutation = useCompleteEnrollmentMutation();
  const isApiCalled = useRef(false);
  const enrollmentStatus = useSelector((state: RootState) => state.enrollment.enrollmentStep);
  const personalDetails = useSelector((state: RootState) => state.personalDetails);
  const [isForceStep, setIsForceStep] = useState(0);
  const handleStepChange = useCallback(() => {
    if (
      enrollmentStatus === EnrollmentSteps.ENROLLMENT_PREFERENCE_INFORMATION ||
      isForceStep === 0
    ) {
      setActiveStep(0);
    }
    if (enrollmentStatus === EnrollmentSteps.ENROLLMENT_AVAILABILITY || isForceStep === 1) {
      setActiveStep(1);
    }
    if (enrollmentStatus === EnrollmentSteps.ENROLLMENT_EMERGENCY_CONTACT || isForceStep === 2) {
      setActiveStep(2);
    }
    profileQuery.refetch();
  }, [setActiveStep, profileQuery, isForceStep]);
  useEffect(() => {
    methods.clearErrors();
    methods.setValue('stepKey', ENROLLMENT_FORM_STEPS2[activeStep].key);
    // Set default values for phoneNumber and partnerId to bypass validation
    // These fields are not required for additional information (Step 2)
    methods.setValue('phoneNumber', '');
    methods.setValue('partnerId', null as any);
    handleStepChange();
  }, [activeStep, methods, isForceStep]);

  const enrollmentUpdateError = hasApiError(ErrorSource.UPDATE_ENROLLMENT)
    ? getApiErrorBySource(ErrorSource.UPDATE_ENROLLMENT)
    : null;

  const completeEnrollmentError = hasApiError(ErrorSource.COMPLETE_ENROLLMENT)
    ? getApiErrorBySource(ErrorSource.COMPLETE_ENROLLMENT)
    : null;

  const handleDismissError = useCallback(() => {
    dispatch(clearApiError(ErrorSource.UPDATE_ENROLLMENT));
    dispatch(clearApiError(ErrorSource.COMPLETE_ENROLLMENT));
  }, [dispatch]);

  const handleBack = useCallback((): void => {
    setActiveStep((prevStep) => {
      dispatch(setEnrollmentStatus(ENROLLMENT_FORM_STEPS2[prevStep - 1].key));
      return Math.max(0, prevStep - 1);
    });
    setIsForceStep((prev) => Math.max(0, prev - 1));
    formContainerRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [setActiveStep]);

  const handleEditSection = useCallback(
    (stepIndex: number): void => {
      setActiveStep(stepIndex);
      formContainerRef.current?.scrollIntoView({ behavior: 'smooth' });
    },
    [setActiveStep]
  );

  const handleFinalSubmit = useCallback(async (): Promise<void> => {
    if (userId) {
      try {
        await completeEnrollmentMutation.mutateAsync({
          memberId: userId,
          payload: { currentNewStatus: UserStatus.PENDING_MATCHING },
        });
        await profileQuery.refetch();
      } catch (error) {
        console.error('Error completing enrollment:', error);
      }
    }
    setTimeout(() => {
      navigate('/application-received', { replace: true });
      dispatch(setEnrollmentStatus(EnrollmentSteps.ENROLLMENT_COMPLETE));
    }, 200);
  }, [navigate, dispatch, userId, completeEnrollmentMutation, profileQuery]);

  const handleNext = useCallback(async (): Promise<void> => {
    try {
      // Define which fields to validate based on the current step
      // This prevents validation errors for phoneNumber and partnerId which are not required for Step 2
      let fieldsToValidate: (keyof EnrollmentFormData)[] = [];
      switch (activeStep) {
        case 0: // Preference Information
          fieldsToValidate = ['hopeToGain'];
          break;
        case 1: // Schedule
          fieldsToValidate = ['shiftTime', 'shiftDays'];
          break;
        case 2: // Emergency Contact
          if (personalDetails?.dob) {
            const calculateAge = (dob: string) => {
              if (!dob) return 0;
              const birthDate = new Date(dob);
              const today = new Date();
              let age = today.getFullYear() - birthDate.getFullYear();
              const monthDiff = today.getMonth() - birthDate.getMonth();
              if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                age--;
              }
              return age;
            };
            const age = calculateAge(personalDetails.dob);
            if (age < 18) {
              fieldsToValidate = ['contactName', 'contactPhone', 'contactRelationship'];
            } else {
              fieldsToValidate = ['contactName', 'contactPhone', 'contactRelationship'];
            }
          }
          break;
      }
      
      // This bypasses validation for phoneNumber and partnerId
      const isValid = fieldsToValidate.length > 0 
        ? await methods.trigger(fieldsToValidate as any)
        : await methods.trigger();

      if (!isValid) {
        console.log('Validation errors:', methods.formState.errors);
        return;
      }

      const formData: EnrollmentFormData = methods.getValues();
      const currentConfig = stepConfig2[activeStep];

      if (!currentConfig) return;

      setIsSaving(true);

      // Remove fields that are not part of Step 2 (additional information)
      // Set phoneNumber and partnerId to undefined to bypass validation
      const cleanedFormData = {
        ...formData,
        phoneNumber: undefined as any,
        partnerId: undefined as any,
      };
      if (!cleanedFormData.isStudent) {
        delete (cleanedFormData as any).school;
      }
      delete (cleanedFormData as any).isStudent;
      if (isApiCalled.current) {
        return;
      }
      isApiCalled.current = true;
      const result = await submitStepData(
        activeStep,
        cleanedFormData,
        currentConfig.updateRedux,
        currentConfig.prepareApiData
      );
      if (activeStep === ENROLLMENT_FORM_STEPS2.length - 1) {
        handleFinalSubmit();
        return;
      }
      if (result.success) {
        isApiCalled.current = false;
        setActiveStep((prevStep) => {
          dispatch(setEnrollmentStatus(ENROLLMENT_FORM_STEPS2[prevStep + 1].key));
          return prevStep + 1;
        });
        setIsForceStep((prev) => prev + 1);
        formContainerRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    } catch (error) {
      isApiCalled.current = false;
      console.error('Error saving step data:', error);
    } finally {
      setIsSaving(false);
    }
  }, [activeStep, methods, submitStepData, setIsSaving, setActiveStep, handleFinalSubmit]);

  useEffect(() => {
    const disableBack = (event: PopStateEvent) => {
      event.preventDefault();
      return false;
    };

    window.history.pushState(null, '', window.location.pathname);
    window.addEventListener('popstate', disableBack);

    return () => {
      window.removeEventListener('popstate', disableBack);
    };
  }, [window.location.pathname]);

  return (
    <ContainerBox sx={{ padding: '0px' }}>
      <FormCard>
        <Box sx={{ display: 'flex', width: '100%' }}>
          <FormProvider {...methods}>
            <FormSidebar
              activeStep={activeStep}
              getProgressValue={getProgressValue}
              options={ENROLLMENT_FORM_STEPS2}
              title="You're almost done!"
              description='Complete your remaining profile to get started'
              hideProgress
            />

            <FormContentContainer ref={formContainerRef}>
              {/* {isMobile && (
                <MobileHeader activeStep={activeStep} getProgressValue={getProgressValue} />
              )} */}
              <form id='enrollment-form' onSubmit={(e) => e.preventDefault()}>
                <StepContent2
                  activeStep={activeStep}
                  isMobile={isMobile}
                  isSaving={isSaving}
                  onEditSection={handleEditSection}
                />
              </form>
              <FormNavigation
                activeStep={activeStep}
                handleBack={handleBack}
                handleNext={handleNext}
                isSaving={isSaving}
                isAdditionalInfo
              />

              {enrollmentUpdateError && (
                <ApiErrorLabel
                  message={enrollmentUpdateError.message}
                  variant='alert'
                  testId='api-error'
                  onDismiss={handleDismissError}
                  dismissible={true}
                />
              )}
              {completeEnrollmentError && (
                <ApiErrorLabel
                  message={completeEnrollmentError.message}
                  variant='alert'
                  testId='api-error'
                  onDismiss={handleDismissError}
                  dismissible={true}
                />
              )}
            </FormContentContainer>
          </FormProvider>
        </Box>
      </FormCard>
    </ContainerBox>
  );
};

export default React.memo(MindRightEnrollment2);
